import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { UpdateUserAvailabilityRequest } from './models/update-user-availability-request';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    Subject,
    takeUntil,
    tap,
} from 'rxjs';
import { UserProfileService } from '../shared/services/user-profile/user-profile.service';
import { UserAvailabilityResponse } from './models/user-availability-response';
import { AlertService } from '../../../../core/services/alert/alert.service';

@Component({
    selector: 'app-user-availability',
    imports: [
        MatDatepickerModule,
        MatIconModule,
        MatInputModule,
        MatSlideToggleModule,
        FormsModule,
        ReactiveFormsModule,
    ],
    providers: [provideNativeDateAdapter()],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './user-availability.component.html',
})
export class UserAvailabilityComponent implements OnInit, OnDestroy {
    @Input() userId: number | undefined = undefined;
    form: FormGroup<FormGroupType<UpdateUserAvailabilityRequest>>;

    private destroy$ = new Subject<void>();
    private initialized = false;
    private lastKnownIsAvailable: boolean | null | undefined = undefined;

    constructor(
        private readonly alertService: AlertService,
        private readonly userProfileService: UserProfileService,
    ) {
        this.form = new FormGroup<FormGroupType<UpdateUserAvailabilityRequest>>(
            {
                isAvailable: new FormControl(false, Validators.required),
                unavailabilityStartDate: new FormControl(null),
                unavailabilityEndDate: new FormControl(null),
            },
            { validators: [this.availabilityValidator()] },
        );
    }

    ngOnInit(): void {
        if (!this.userId) {
            throw new Error(
                'User ID input is required to get and update user availability.',
            );
        }

        this.userProfileService
            .getUserAvailability(this.userId)
            .pipe(
                takeUntil(this.destroy$),
                tap((response) => {
                    this.form.patchValue(
                        {
                            isAvailable: response.isAvailable,
                        },
                        { emitEvent: false },
                    );

                    this.setUnavailabilityDates(response);
                    this.lastKnownIsAvailable =
                        this.form.controls.isAvailable.value;
                    this.initialized = true;
                }),
            )
            .subscribe();

        this.form.controls.unavailabilityEndDate.valueChanges
            .pipe(
                filter(() => this.initialized),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.checkAvailability();
            });

        this.form.valueChanges
            .pipe(
                filter(() => this.initialized),
                debounceTime(150),
                distinctUntilChanged(
                    (previous, current) =>
                        JSON.stringify(previous) === JSON.stringify(current),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((value) => {
                const current = value as UpdateUserAvailabilityRequest;
                const previousIsAvailable = this.lastKnownIsAvailable;
                const isAvailableChangedToTrue =
                    previousIsAvailable !== current.isAvailable &&
                    current.isAvailable === true;

                if (
                    isAvailableChangedToTrue &&
                    this.areUnavailabilityDatesCurrent()
                ) {
                    // Revert the change to isAvailable if the unavailability dates include today's date
                    this.form.controls.isAvailable.setValue(false, {
                        emitEvent: false,
                    });
                    this.lastKnownIsAvailable = false;
                    return;
                }

                this.lastKnownIsAvailable = current.isAvailable;
                this.saveChanges(current);
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    clearDates(): void {
        if (
            this.form.controls.unavailabilityStartDate?.value ||
            this.form.controls.unavailabilityEndDate?.value
        ) {
            this.form.patchValue(
                {
                    isAvailable: true,
                    unavailabilityStartDate: null,
                    unavailabilityEndDate: null,
                },
                { emitEvent: true },
            );
        }
    }

    private setUnavailabilityDates(response: UserAvailabilityResponse) {
        const startDate = response.unavailabilityStartDate;
        const endDate = response.unavailabilityEndDate;

        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        if (endDate && currentDate <= new Date(endDate)) {
            this.form.patchValue(
                {
                    unavailabilityStartDate: startDate,
                    unavailabilityEndDate: endDate,
                },
                { emitEvent: false },
            );
        }
    }

    private checkAvailability(): void {
        const previousIsAvailable = this.form.controls.isAvailable.value;
        let startDate = this.form.controls.unavailabilityStartDate?.value;
        let endDate = this.form.controls.unavailabilityEndDate?.value;
        let isAvailable = true;

        if (startDate && endDate) {
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);
            startDate = new Date(startDate);
            endDate = new Date(endDate);
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);

            isAvailable = currentDate < startDate || currentDate > endDate;
        } else {
            isAvailable = true;
        }

        const availabilityChanged = previousIsAvailable !== isAvailable;

        if (availabilityChanged) {
            this.form.controls.isAvailable.setValue(isAvailable, {
                emitEvent: false,
            });
        }
    }

    private areUnavailabilityDatesCurrent(): boolean {
        let startDate = this.form.controls.unavailabilityStartDate?.value;
        let endDate = this.form.controls.unavailabilityEndDate?.value;

        if (startDate && endDate) {
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);
            startDate = new Date(startDate);
            endDate = new Date(endDate);
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);

            const areUnavailabilityDatesCurrent =
                currentDate >= startDate && currentDate <= endDate;

            if (areUnavailabilityDatesCurrent) {
                this.alertService.displayWarningAlert(
                    "You cannot be marked available if the unavailability dates include today's date.",
                );

                return true;
            }
        }

        return false;
    }

    private availabilityValidator(): ValidatorFn {
        return (group: AbstractControl): ValidationErrors | null => {
            const form = group as FormGroup<
                FormGroupType<UpdateUserAvailabilityRequest>
            >;
            const start = form.controls.unavailabilityStartDate?.value;
            const end = form.controls.unavailabilityEndDate?.value;
            return start && !end ? { endDateRequired: true } : null;
        };
    }

    private displaySuccessMessage(): void {
        this.alertService.displaySuccessAlert(
            'Your availability has been updated successfully.',
        );
    }

    private saveChanges(request: UpdateUserAvailabilityRequest) {
        if (this.form.valid) {
            this.userProfileService
                .updateUserAvailability(this.userId!, request)
                .pipe(
                    takeUntil(this.destroy$),
                    tap(() => {
                        this.displaySuccessMessage();
                    }),
                )
                .subscribe();
        }
    }
}
